
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { User } from "@/types";
import { Send } from "lucide-react";

interface TransferFormProps {
  children: User[];
  currentUserId: string;
  currentUserRole: 'parent' | 'child';
  onTransfer: (toUserId: string, amount: number, description: string) => Promise<void>;
}

interface TransferFormData {
  toUserId: string;
  amount: number;
  description: string;
}

export function TransferForm({ children, currentUserId, currentUserRole, onTransfer }: TransferFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<TransferFormData>();
  
  // Filter recipients based on current user role
  const availableRecipients = children.filter(child => {
    // Exclude current user
    if (child.id === currentUserId) return false;
    
    // If current user is a child, only show other children
    if (currentUserRole === 'child') {
      return child.role === 'child';
    }
    
    // If current user is a parent, show all family members
    return true;
  });
  
  const onSubmit = async (data: TransferFormData) => {
    setIsLoading(true);
    try {
      await onTransfer(data.toUserId, data.amount, data.description);
      reset();
      setIsOpen(false);
    } catch (error) {
      console.error("Transfer failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="flex items-center gap-2">
          <Send className="h-4 w-4" />
          {currentUserRole === 'child' ? 'Request Transfer' : 'Transfer Money'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {currentUserRole === 'child' ? 'Request Transfer' : 'Transfer Money'}
          </DialogTitle>
          <DialogDescription>
            {currentUserRole === 'child' 
              ? "Request a transfer to another child. This will need parent approval."
              : "Send money to another family member."
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="toUserId">Send to</Label>
            <Select onValueChange={(value) => setValue("toUserId", value)} required>
              <SelectTrigger>
                <SelectValue placeholder={
                  currentUserRole === 'child' 
                    ? "Select a child" 
                    : "Select family member"
                } />
              </SelectTrigger>
              <SelectContent>
                {availableRecipients.map((recipient) => (
                  <SelectItem key={recipient.id} value={recipient.id}>
                    {recipient.firstName} {recipient.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="amount">Amount (₪)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              {...register("amount", { 
                required: "Amount is required",
                min: { value: 0.01, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.amount && (
              <p className="text-sm text-destructive">{errors.amount.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              {...register("description")}
              placeholder="What's this transfer for?"
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading 
                ? (currentUserRole === 'child' ? "Requesting..." : "Transferring...") 
                : (currentUserRole === 'child' ? "Request Transfer" : "Transfer")
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
